import {useApp} from "../app.context";
import {NavTarget} from "../utils/navTarget";

// Capture-phase click props that intercept a Ctrl/Cmd+click and navigate,
// BEFORE the element's own onClick (description-edit, drag start, asset popup) —
// so existing behaviors are untouched and a plain click passes straight through.
// Non-hook form for use inside list/map callbacks where hooks can't be called;
// pass navigateTo from useApp(). A no-op when target is undefined.
export function navTargetProps(navigateTo: (t: NavTarget) => void, target?: NavTarget) {
    if (!target) return {};
    return {
        onClickCapture: (e: React.MouseEvent) => {
            if (!(e.metaKey || e.ctrlKey)) return;
            const el = e.target as HTMLElement;
            const root = e.currentTarget as HTMLElement;
            // Only the reference's label text is the link, not the whole row.
            // Interactive controls keep their own action (edit / delete / pick),
            // and the icon / gaps / empty space aren't clickable. Icon-only
            // references (no text, e.g. tech-tree unlock icons) have nothing to
            // scope to, so the whole small element stays navigable.
            // Mirrors the hover CSS in App.css.
            if (el.closest('button, input, textarea, select')) return;
            const hasText = !!root.querySelector('p, label');
            if (hasText && !el.closest('p, label')) return;
            e.preventDefault();
            e.stopPropagation();
            navigateTo(target);
        },
        // Marks this element as navigable. While the Ctrl/Cmd modifier is held
        // (body.modifier-nav), hovering it shows the link cursor + styling —
        // see App.css. Only present when there's actually a target.
        'data-navref': true,
    };
}

// Hook form: spread the result onto a label/row element.
export function useNavTarget(target?: NavTarget) {
    const {navigateTo} = useApp();
    return navTargetProps(navigateTo, target);
}
